import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { fetchExecutionsPerUser } from '../../../../../modules/dashboard';
import NoDataAvailable from './NoDataAvailable';
import { createQueryString } from '../../../../../lib/utils';
import ChartTitle from '../../../../elements/ChartTitle';
import { Spinner } from 'react-bootstrap';

const GRAPH_TITLE = 'Executions By Engineer';

const ExecutionsPerUser = ({ projectId, dateFilters }) => {
  const queryString = createQueryString(dateFilters);

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['executionsPerUser', projectId, queryString],
    queryFn: () => fetchExecutionsPerUser(projectId, queryString),
    enabled: !!projectId,
  });

  const { chartData, statusKeys, statusColors } = useMemo(() => {
    if (!response?.data?.length)
      return { chartData: [], statusKeys: [], statusColors: {} };

      const colorMapping = {};
      const orderedStatuses = response?.status_meta.map(({ status, color }) => {
        colorMapping[status] = color;
        return status;
      });

      const transformedData = response?.data.map(({ user_name, statuses }) => ({
        user: user_name,
        ...Object.fromEntries(
          statuses?.map(({ status, count }) => [status, count])
        ),
      }));

    return {
      chartData: transformedData,
      statusKeys: orderedStatuses,
      statusColors: colorMapping,
    };
  }, [response]);

  return (
    <div className="chart-div">
      <ChartTitle title={GRAPH_TITLE} dateFilters={dateFilters} />
      {isLoading ? (
        <Spinner className="mt-5" />
      ) : chartData.length === 0 || error ? (
        <NoDataAvailable />
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            barSize={25}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="user"
              label={{
                value: 'Engineers',
                position: 'insideBottom',
                offset: -10,
              }}
              tick={{ fontSize: 12 }}
            />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend  wrapperStyle={{ padding: 10 }}/>

            {statusKeys.map((status) => (
              <Bar
                key={status}
                dataKey={status}
                stackId="a"
                fill={statusColors[status] || '#007bff'}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default ExecutionsPerUser;
